'use client';

import {
  FC,
  useEffect,
  useRef,
  useState,
  VideoHTMLAttributes,
  RefObject,
} from 'react';
import {
  normalizeTrackingValue,
  trackEvent,
  useVideoFullscreenListener,
  VideoEvent,
} from '@tuic/tracking';
import { PosterResolverReturnValue } from '../types';
import { twMerge } from '@tuic/tailwind-config';
import { applyContentfulCdnProxyIfEnabled } from '../../../../utils';

export type NativeVideoProps = VideoHTMLAttributes<HTMLVideoElement> & {
  videoProps: VideoHTMLAttributes<HTMLVideoElement> | undefined;
  posterData: PosterResolverReturnValue | undefined;
  userPlay: boolean;
  forwardedRef?: RefObject<HTMLVideoElement | null>;
};

/**
 * Extracts the video title and ID from the video source URL
 *
 * Assumes the video source is from Contentful:
 * The last part of the URL is the filename which corresponds to the video title.
 * The second last part is the Asset ID of the video.
 *
 * @param src The video source URL
 * @returns The video title and ID
 */
const getVideoData = (src: string) => {
  const parts = src.split('/');
  const title = parts.pop() || src;
  const id = parts.pop() || src;

  return { title: normalizeTrackingValue(title), id };
};

const getPosterSource = (
  posterData: PosterResolverReturnValue | undefined,
): string | undefined => {
  if (typeof posterData === 'undefined') {
    return undefined;
  }

  if (typeof posterData.src === 'string') {
    return posterData.src;
  }

  if ('src' in posterData.src) {
    return posterData.src.src;
  }

  return undefined;
};

export const NativeVideo: FC<NativeVideoProps> = ({
  videoProps,
  posterData,
  userPlay,
  autoPlay,
  forwardedRef,
  ...attrs
}) => {
  const internalRef = useRef<HTMLVideoElement>(null);
  const nativePlayerRef = forwardedRef || internalRef;
  const [videoTitle, setVideoTitle] = useState<string>('');
  const [videoId, setVideoId] = useState<string>('');

  useVideoFullscreenListener(nativePlayerRef, videoTitle, videoId);

  useEffect(() => {
    if (userPlay && nativePlayerRef.current) {
      nativePlayerRef.current.play();
    }
  }, [userPlay]);

  useEffect(() => {
    if (videoProps?.src) {
      const { title, id } = getVideoData(String(videoProps.src));
      setVideoTitle(title);
      setVideoId(id);
    }
  }, [videoProps]);

  useEffect(() => {
    const player = nativePlayerRef.current;
    if (!player) return;

    const onPlaybackChange = (eventName: VideoEvent['event_name']) => {
      trackEvent({
        event_name: eventName,
        video_name: videoTitle,
        video_id: videoId,
      });
    };

    const onPlay = () => onPlaybackChange('video-play');
    const onPause = () => onPlaybackChange('video-pause');
    const onEnded = () => onPlaybackChange('video-completed');

    player.addEventListener('play', onPlay);
    player.addEventListener('pause', onPause);
    player.addEventListener('ended', onEnded);

    return () => {
      player.removeEventListener('play', onPlay);
      player.removeEventListener('pause', onPause);
      player.removeEventListener('ended', onEnded);
    };
  }, [nativePlayerRef, videoTitle, videoId]);

  return (
    <>
      <video
        {...attrs}
        {...videoProps}
        src={applyContentfulCdnProxyIfEnabled(videoProps?.src ?? attrs.src)}
        poster={applyContentfulCdnProxyIfEnabled(getPosterSource(posterData))}
        ref={nativePlayerRef}
        width="100%"
        height="100%"
        className={twMerge(videoProps?.className, 'h-full w-full object-cover')}
        preload={typeof posterData !== 'undefined' ? 'none' : 'auto'}
        autoPlay={autoPlay}
        controls={
          autoPlay || userPlay
            ? (videoProps?.controls ?? attrs.controls)
            : false
        }
      />
    </>
  );
};
